import { baseUrl } from '../../../constants';
import useLoginUser from '../../../hooks/useLoginUser';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import RetryLoader from '../../../components/commons/retryLoader';
import { numberWithCommas } from '../../../utilities';

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Pie,
  PieChart
} from 'recharts';

import ReactExport from 'react-export-excel';
import localforage from 'localforage';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const Reports = () => {
  const { user } = useLoginUser();

  const [dashboard, setDashboard] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState(false);

  const fectchDashboardData = async force => {
    setLoading(true);
    let cacheData = await localforage.getItem('dashboard-data');

    if (cacheData && !force) {
      setDashboard(cacheData);
      setGraph(cacheData);
      setLoading(false);
    }
    fetch(
      `${baseUrl}/dashboard${
        user?.is_super_admin ? '' : '/' + user?.id
      }?period=${period}`
    )
      .then(dashboard => dashboard.json())
      .then(dashboard => {
        if (dashboard.error || dashboard.errors)
          throw new Error(dashboard.message);
        dashboard.totalTransactionAmount = numberWithCommas(
          dashboard.totalTransactionAmount
        );
        localforage.setItem('dashboard-data', dashboard);
        setDashboard(dashboard);
        setGraph(dashboard);
        setLoading(false);
        setError(false);
      })
      .catch(err => {
        toast.error('An error occurred, please try again');
        setError(true);
        setLoading(false);
        console.error(err);
      });
  };

  useEffect(() => {
    if (!period) return;
    fectchDashboardData(true);
    // eslint-disable-next-line
  }, [period]);

  const setGraph = data => {
    setChart(JSON.parse(JSON.stringify(data)));
    const graphData = data.graphData || {};
    const finalData = Object.keys(graphData).map(key => {
      graphData[key]['name'] = key;
      return graphData[key];
    });
    setGraphData(finalData);
  };

  const setChart = data => {
    const chartData = data.chartData || {};
    const finalData = {};
    Object.keys(chartData).forEach(key => {
      finalData[key] = Object.keys(chartData[key]).map(name => ({
        name,
        value: chartData[key][name]
      }));
    });
    setChartData(finalData);
  };

  useEffect(() => {
    fectchDashboardData();
    // eslint-disable-next-line
  }, []);

  return (
    <div className='dashboard-home container'>
      <RetryLoader
        retry={fectchDashboardData}
        loading={loading}
        error={error}
        fullscreen={false}
      >
        <div className='row'>
          <div className='col-md-4 mb-3'>
            <div className='dashboard-home__card dashboard-home__card-amount'>
              <svg
                viewBox='0 0 512 512'
                width='512'
                xmlns='http://www.w3.org/2000/svg'
              >
                <g>
                  <g>
                    <path d='m498.667 366.464c-9.551-14.036-25.752-17.463-43.352-9.181l-9.328 4.107c14.708-31.35 16.533-66.297 9.211-99.307-8.409-37.913-28.72-74.641-60.368-109.164-2.798-3.053-7.541-3.259-10.595-.46-3.053 2.798-3.259 7.541-.46 10.595 29.846 32.556 48.95 66.967 56.782 102.276 8.249 37.193 3.45 75.549-17.601 106.2l-93.342 41.099.077-10.542c.002-.266-.01-.532-.037-.797-2.155-21.667-18.717-38.93-40.276-41.98-.064-.01-.128-.018-.193-.025l-82.961-9.552c-31.901-4.541-40.117-23.658-83.321-34.559-2.985-25.33.994-52.299 11.9-79.336 16.425-40.718 48.558-80.278 93.104-114.711 16.603 11.772 90.676 13.237 107.252-1.949 8.492 6.449 16.597 13.095 24.147 19.822 1.429 1.274 3.211 1.9 4.986 1.9 2.064 0 4.12-.847 5.601-2.51 2.755-3.092 2.481-7.832-.611-10.587-8.276-7.373-17.178-14.648-26.515-21.679 2.188-9.278-.874-20.137-8.954-26.601 23.479-35.612 30.308-58.921 20.875-71.133-6.479-8.389-14.539-4.452-17.981-2.77-5.834 2.848-9.015 2.998-14.383-.514-10.241-6.701-21.005-6.917-31.576 0-5.436 3.557-9.717 3.557-15.151 0-10.242-6.701-21.002-6.917-31.575 0-5.43 3.554-8.623 3.334-14.365.48-3.438-1.709-11.489-5.711-18.009 2.679-9.221 11.868-3.052 34.442 18.843 68.843-1.341.725-2.619 1.576-3.812 2.548-8.708-9.196-22.975-18.787-43.607-16.483-17.113 1.915-29.732-3.74-37.306-8.82-3.44-2.304-8.098-1.386-10.404 2.052-2.306 3.44-1.387 8.098 2.052 10.404 9.655 6.473 25.701 13.679 47.322 11.268 15.94-1.788 26.756 6.358 33.489 14.648-.092.32-.177.642-.256.964-5.743.09-14.326.626-23.778 2.592-22.732 4.729-39.606 15.532-48.799 31.244-2.091 3.574-.889 8.168 2.685 10.26 3.575 2.091 8.168.888 10.259-2.686 13.674-23.369 47.051-26.227 60.308-26.406.057.165.124.328.184.492-47.308 36.493-80.244 77.19-97.932 121.04-11.18 27.717-15.646 55.485-13.379 81.874-7.191-1.064-14.465-1.635-21.774-1.685v-4.535c0-11.999-9.762-21.76-21.761-21.76h-34.424c-11.999 0-21.761 9.762-21.761 21.76v174.644c0 12 9.762 21.761 21.761 21.761h34.423c11.999 0 21.761-9.762 21.761-21.761v-2.136l75.091 27.949c10.091 3.755 20.667 5.66 31.434 5.66h111.886c17.106 0 33.785-4.84 48.233-13.995 149.259-94.62 140.195-88.733 141.057-89.497 12.023-10.672 14.269-28.746 5.224-42.04zm-177.58-256.172c-27.422 6.924-54.084 6.924-81.512 0-11.769-2.975-8.359-23.965 4.289-20.793 24.538 6.16 48.394 6.16 72.934 0 12.428-3.12 16.247 17.771 4.289 20.793zm-98.279-91.361c.059.029.118.059.174.087 10.149 5.044 19.042 5.318 29.251-1.361 12.776-8.363 14.163 5.046 30.938 5.046 8.076 0 12.533-2.916 15.787-5.046 5.437-3.558 9.719-3.556 15.155 0 10.254 6.71 18.932 6.379 29.376 1.342 1.543 5.562-1.949 22.143-24.185 55.249-11.042-.188-27.066 10.657-72.428.562-22.188-33.534-25.619-50.276-24.068-55.879zm261.085 377.99-139.534 88.417c-12.043 7.631-25.946 11.666-40.205 11.666h-111.886c-8.975 0-17.791-1.588-26.203-4.719l-80.323-29.897v-80.755c0-4.142-3.357-7.499-7.498-7.499s-7.498 3.357-7.498 7.499v98.894c0 3.73-3.035 6.764-6.764 6.764h-34.424c-3.73 0-6.764-3.035-6.764-6.764v-174.644c0-3.73 3.034-6.763 6.764-6.763h34.423c3.73 0 6.764 3.034 6.764 6.763v40.694c0 4.142 3.357 7.499 7.498 7.499s7.498-3.357 7.498-7.499v-21.162c62.031.475 75.978 33.17 118.476 39.181.064.01.128.018.192.025l82.957 9.551c14.526 2.097 25.705 13.664 27.323 28.227l-.104 14.264h-77.365c-4.141 0-7.499 3.357-7.499 7.499s3.357 7.499 7.499 7.499h84.809c.947 0 2.041-.21 2.993-.625.153-.068 136.263-59.995 136.422-60.065 9.811-4.36 18.756-4.983 24.822 3.931 4.716 6.927 3.672 16.292-2.373 22.019z' />
                    <path d='m282.307 340.22c4.141 0 7.499-3.357 7.499-7.499v-12.43c21.051-3.416 33.334-20.455 36.006-36.351 3.338-19.857-7.063-37.126-26.497-43.995-3.434-1.214-6.594-2.375-9.51-3.496v-47.812c8.871 1.471 14.197 6.062 14.585 6.405 3.046 2.77 7.76 2.565 10.555-.465 2.808-3.044 2.616-7.788-.428-10.596-.529-.488-9.713-8.757-24.712-10.486v-10.664c0-4.142-3.357-7.499-7.499-7.499-4.141 0-7.498 3.357-7.498 7.499v11.27c-1.808.346-3.66.786-5.563 1.359-12.72 3.831-22.228 14.738-24.815 28.463-2.347 12.455 1.602 24.433 10.305 31.259 4.997 3.919 11.287 7.507 20.073 11.343v59.301c-8.672-.367-14.01-1.995-23.322-8.087-3.465-2.266-8.113-1.297-10.38 2.17-2.267 3.465-1.296 8.113 2.17 10.38 12.241 8.008 20.424 10.097 31.532 10.529v11.903c0 4.142 3.357 7.499 7.499 7.499zm-18.316-116.838c-4.281-3.358-6.13-9.75-4.823-16.681 1.212-6.428 5.631-14.238 14.403-16.88.417-.126.827-.234 1.237-.344v40.505c-4.49-2.242-8.011-4.399-10.817-6.6zm30.326 30.703c18.66 6.595 17.504 22.617 16.705 27.37-1.654 9.841-8.878 20.347-21.217 23.509v-52.504c1.46.534 2.951 1.073 4.512 1.625z' />
                  </g>
                </g>
              </svg>
              <div className='dashboard-home__card__texts'>
                <h1 className='dashboard-home__card__texts__heading'>
                  ₦{dashboard?.totalTransactionAmount || 0}
                </h1>
                <p className='dashboard-home__card__texts__sub-heading'>
                  Total Transactions
                </p>
              </div>
            </div>
          </div>
          <div className='col-md-4 mb-3'>
            <div className='dashboard-home__card dashboard-home__card-travels'>
              <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'>
                <path d='M240.808,53.284a6.71,6.71,0,0,0,4.913,1.735,219.643,219.643,0,0,1,30.826.725q.328.028.651.029a6.867,6.867,0,0,0,.638-13.7,233.117,233.117,0,0,0-32.758-.769,6.88,6.88,0,0,0-4.27,11.984Z' />
                <path d='M173.164,70.684a6.829,6.829,0,0,0,2.552-.5,215.013,215.013,0,0,1,29.352-9.372,6.867,6.867,0,0,0-3.225-13.351,228.585,228.585,0,0,0-31.235,9.975,6.868,6.868,0,0,0,2.556,13.243Z' />
                <path d='M139.231,89.031a6.868,6.868,0,1,0-7.436-11.548,231.436,231.436,0,0,0-26.221,19.655,6.867,6.867,0,1,0,9,10.377A217.73,217.73,0,0,1,139.231,89.031Z' />
                <path d='M337.925,45.33h0a7.106,7.106,0,1,0,1.282,9.971A7.117,7.117,0,0,0,337.924,45.33Z' />
                <path d='M355.469,58.882h0a7.11,7.11,0,1,0,2.707,4.723A7.118,7.118,0,0,0,355.468,58.881Z' />
                <path d='M373.013,72.433h0a7.108,7.108,0,1,0,2.708,4.722A7.082,7.082,0,0,0,373.012,72.433Z' />
                <path d='M390.563,85.989l0,0a7.109,7.109,0,1,0,1.281,9.972A7.115,7.115,0,0,0,390.558,85.985Z' />
                <path d='M306.939,480.718a215.864,215.864,0,0,1-30.4,5.076,6.868,6.868,0,0,0,.637,13.705q.323,0,.652-.03a230.435,230.435,0,0,0,32.333-5.4,6.868,6.868,0,0,0-3.227-13.352Z' />
                <path d='M235.464,485.8a216.214,216.214,0,0,1-30.4-5.075,6.868,6.868,0,0,0-3.226,13.352,230.181,230.181,0,0,0,32.334,5.4c.219.02.435.03.65.03a6.868,6.868,0,0,0,.638-13.7Z' />
                <path d='M438.7,134.744a6.868,6.868,0,0,0-6.725,10.761A216.87,216.87,0,0,1,448.034,171.8a6.867,6.867,0,1,0,12.2-6.3,230.455,230.455,0,0,0-17.075-27.965A6.824,6.824,0,0,0,438.7,134.744Z' />
                <path d='M372.761,452.51a215.747,215.747,0,0,1-27.022,14.781,6.868,6.868,0,1,0,5.712,12.493A229.933,229.933,0,0,0,380.2,464.059a6.868,6.868,0,0,0-7.435-11.549Z' />
                <path d='M435.427,403.15a6.866,6.866,0,0,0-9.644,1.146A217.941,217.941,0,0,1,405.037,427.1a6.868,6.868,0,0,0,9.479,9.941,231.616,231.616,0,0,0,22.057-24.249A6.874,6.874,0,0,0,435.427,403.15Z' />
                <path d='M469.821,240.029a6.9,6.9,0,0,0,6.791,5.9,7.192,7.192,0,0,0,.976-.069,6.876,6.876,0,0,0,5.83-7.768,229.26,229.26,0,0,0-6.937-32.043,6.867,6.867,0,0,0-13.18,3.864A215.578,215.578,0,0,1,469.821,240.029Z' />
                <path d='M468.906,337.18a6.87,6.87,0,0,0-8.736,4.245,215.218,215.218,0,0,1-12.149,28.337,6.868,6.868,0,1,0,12.2,6.3,229.119,229.119,0,0,0,12.926-30.151,6.867,6.867,0,0,0-4.244-8.735Z' />
                <path d='M35.838,235.325a6.865,6.865,0,0,0,8.037-5.453A215.287,215.287,0,0,1,51.832,200.1a6.867,6.867,0,0,0-12.98-4.49,228.989,228.989,0,0,0-8.467,31.675,6.869,6.869,0,0,0,5.453,8.037Z' />
                <path d='M166.263,467.294a216.4,216.4,0,0,1-27.048-14.8,6.867,6.867,0,0,0-7.437,11.547,230.147,230.147,0,0,0,28.771,15.741,6.792,6.792,0,0,0,2.853.625,6.869,6.869,0,0,0,2.861-13.115Z' />
                <path d='M42.18,301.512a218.689,218.689,0,0,1-2.17-30.753,6.868,6.868,0,1,0-13.735.01,232.414,232.414,0,0,0,2.308,32.681,6.9,6.9,0,0,0,6.79,5.9,7.207,7.207,0,0,0,.977-.069A6.878,6.878,0,0,0,42.18,301.512Z' />
                <path d='M478.858,263.9a6.876,6.876,0,0,0-6.868,6.867,218.906,218.906,0,0,1-2.17,30.751,6.868,6.868,0,0,0,5.83,7.768,7.185,7.185,0,0,0,.976.069,6.9,6.9,0,0,0,6.791-5.9,232.685,232.685,0,0,0,2.308-32.688A6.875,6.875,0,0,0,478.858,263.9Z' />
                <path d='M51.827,341.419a6.867,6.867,0,0,0-12.98,4.491,229.176,229.176,0,0,0,12.918,30.135,6.867,6.867,0,1,0,12.2-6.3A215.466,215.466,0,0,1,51.827,341.419Z' />
                <path d='M86.2,404.277a6.867,6.867,0,1,0-10.791,8.5,231.718,231.718,0,0,0,22.051,24.245,6.868,6.868,0,0,0,9.48-9.939A217.9,217.9,0,0,1,86.2,404.277Z' />
                <path d='M85.071,127.6a6.878,6.878,0,0,0-9.644,1.146,230.97,230.97,0,0,0-18.4,27.138,6.87,6.87,0,0,0,5.938,10.308,6.9,6.9,0,0,0,5.95-3.43,216.84,216.84,0,0,1,17.3-25.516A6.879,6.879,0,0,0,85.071,127.6Z' />
                <path d='M408.393,163.151a10.214,10.214,0,0,0,10.782-3.624,6,6,0,0,0,.965-1.833l9.969-31.043,27.578-17.566a5.976,5.976,0,0,0,1.526-1.393A10.242,10.242,0,0,0,450.754,91.2L418.7,86.914,402.66,74.529l22.03-28.522a9.694,9.694,0,0,0-1.745-13.6l-6.9-5.33a13.427,13.427,0,0,0-16.975.492l-26.911,23.4L329.612,18.105a24.474,24.474,0,0,0-29.921,38.738l42.546,32.863L326.4,121.658a13.425,13.425,0,0,0,3.811,16.545l6.9,5.333a9.622,9.622,0,0,0,5.906,2.023,9.84,9.84,0,0,0,1.255-.081,9.632,9.632,0,0,0,6.437-3.689l22.031-28.523,16.041,12.39,12.3,29.863A10.209,10.209,0,0,0,408.393,163.151ZM399.265,119.6a6,6,0,0,0-1.88-2.463L366.554,93.327a6,6,0,1,0-7.336,9.5l4.024,3.108-20.622,26.7-5.078-3.923a1.394,1.394,0,0,1-.4-1.717L355.173,90.63a6,6,0,0,0-1.708-7.413L307.026,47.346A12.474,12.474,0,0,1,322.277,27.6l46.439,35.87a6,6,0,0,0,7.605-.221l30.621-26.626a1.394,1.394,0,0,1,1.766-.051l5.077,3.921-20.622,26.7-4.024-3.108a6,6,0,1,0-7.335,9.5L412.635,97.4a6,6,0,0,0,2.873,1.2l30.017,4.01-23.69,15.089a6,6,0,0,0-2.489,3.225l-8.56,26.657Z' />
                <path d='M256,126.809c-79.38,0-143.96,64.58-143.96,143.959S176.62,414.728,256,414.728s143.96-64.58,143.96-143.96S335.38,126.809,256,126.809ZM124.04,270.768c0-71.062,56.465-129.174,126.884-131.852l6.141,37.341a8.84,8.84,0,0,1-3.876,8.822l-23.173,15.174a20.765,20.765,0,0,0-9.048,21.335l3.115,16.312a20.914,20.914,0,0,0,11.424,14.862l16.281,7.839a8.856,8.856,0,0,1,4.86,9.521l-5.932,32.945a8.871,8.871,0,0,1-1.62,3.725,8.83,8.83,0,0,1-15.08-1.568l-5.968-12.843a20.9,20.9,0,0,0-18.891-12.053H185.028a20.729,20.729,0,0,0-11.968,3.781L156.835,295.5a8.819,8.819,0,0,1-7.113,1.365l-24.108-5.723A132.353,132.353,0,0,1,124.04,270.768Zm199.618,71.838,4.23,38.767A131.176,131.176,0,0,1,256,402.728c-61.247,0-112.878-41.944-127.688-98.615l18.638,4.425a20.846,20.846,0,0,0,16.78-3.218l16.224-11.389a8.787,8.787,0,0,1,5.074-1.6h24.129a8.86,8.86,0,0,1,8.009,5.11l5.967,12.843a20.661,20.661,0,0,0,16.737,11.941,21.45,21.45,0,0,0,2.214.115,20.641,20.641,0,0,0,16.622-8.357,20.929,20.929,0,0,0,3.82-8.786l5.932-32.945a20.891,20.891,0,0,0-11.464-22.46l-16.281-7.839a8.867,8.867,0,0,1-4.843-6.3l-3.115-16.313a8.8,8.8,0,0,1,3.836-9.044l23.172-15.174a20.848,20.848,0,0,0,9.143-20.808L263.1,139a131.793,131.793,0,0,1,94.544,47.7l-10.2,7.264a30.423,30.423,0,0,0-11.81,17.217l-3.24,12.657a30.385,30.385,0,0,0,10.727,31.474,18.469,18.469,0,0,1,6.619,18.508l-6.547,29.2a18.352,18.352,0,0,1-5,9.04l-5.709,5.653A30.226,30.226,0,0,0,323.658,342.606Zm64.3-71.838a131.76,131.76,0,0,1-48.89,102.45L335.587,341.3a18.292,18.292,0,0,1,5.339-15.056l5.708-5.653a30.331,30.331,0,0,0,8.267-14.941l6.547-29.2a30.522,30.522,0,0,0-10.939-30.588,18.386,18.386,0,0,1-6.491-19.044l3.24-12.657a18.4,18.4,0,0,1,7.146-10.417l10.468-7.455A131.211,131.211,0,0,1,387.96,270.768Z' />
              </svg>

              <div className='dashboard-home__card__texts'>
                <h1 className='dashboard-home__card__texts__heading'>
                  {dashboard?.totalTravels}
                </h1>
                <p className='dashboard-home__card__texts__sub-heading'>
                  Travel Inquires
                </p>
              </div>
            </div>
          </div>
          <div className='col-md-4 mb-3'>
            <div className='dashboard-home__card dashboard-home__card-certificate'>
              <svg
                viewBox='0 0 479 479.84995'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='m293.773438 195.351562-72.097657 68.097657-34.601562-35.597657c-3.835938-3.949218-10.152344-4.039062-14.097657-.203124-3.949218 3.839843-4.039062 10.152343-.203124 14.101562l41.5 42.601562c3.855468 3.878907 10.085937 4.011719 14.101562.296876l79.300781-74.796876c1.925781-1.816406 3.050781-4.324218 3.125-6.96875.074219-2.644531-.90625-5.210937-2.726562-7.132812-3.859375-4.011719-10.222657-4.1875-14.300781-.398438zm0 0' />
                <path d='m240.273438 90.550781c-82.398438 0-149.398438 67-149.398438 149.398438 0 82.402343 67 149.402343 149.398438 149.402343 82.402343 0 149.402343-67 149.402343-149.402343 0-82.398438-67-149.398438-149.402343-149.398438zm0 278.800781c-71.398438 0-129.398438-58.101562-129.398438-129.402343s58-129.398438 129.398438-129.398438c71.46875 0 129.402343 57.933594 129.402343 129.398438 0 71.464843-57.933593 129.402343-129.402343 129.402343zm0 0' />
                <path d='m445.875 184.949219 3.300781-59.300781c.230469-3.980469-1.933593-7.71875-5.5-9.5l-52.800781-26.699219-26.699219-52.800781c-1.816406-3.535157-5.53125-5.6875-9.5-5.5l-59.402343 3.203124-49.5-32.703124c-3.335938-2.199219-7.660157-2.199219-11 0l-49.5 32.703124-59.296876-3.300781c-3.984374-.234375-7.71875 1.929688-9.5 5.5l-26.703124 52.800781-52.796876 26.699219c-3.539062 1.8125-5.6875 5.527344-5.5 9.5l3.296876 59.300781-32.699219 49.5c-2.199219 3.335938-2.199219 7.660157 0 11l32.601562 49.597657-3.300781 59.300781c-.230469 3.980469 1.929688 7.71875 5.5 9.5l52.800781 26.699219 26.699219 52.800781c1.8125 3.539062 5.527344 5.6875 9.5 5.5l59.300781-3.300781 49.5 32.699219c3.316407 2.269531 7.683594 2.269531 11 0l49.5-32.699219 59.300781 3.300781c3.980469.230469 7.714844-1.933594 9.5-5.5l26.699219-52.800781 52.800781-26.699219c3.535157-1.816406 5.6875-5.527344 5.5-9.5l-3.300781-59.300781 32.699219-49.5c2.199219-3.335938 2.199219-7.664063 0-11zm-18.601562 101.800781c-1.21875 1.789062-1.785157 3.941406-1.597657 6.101562l3.097657 56.097657-49.898438 25.199219c-1.902344.953124-3.445312 2.5-4.398438 4.402343l-25.203124 49.898438-56.097657-3.097657c-2.148437-.078124-4.269531.476563-6.101562 1.597657l-46.800781 30.902343-46.796876-30.902343c-1.625-1.097657-3.539062-1.691407-5.5-1.699219h-.5l-56.101562 3.101562-25.199219-49.902343c-.953125-1.902344-2.496093-3.445313-4.402343-4.398438l-50-25.101562 3.101562-56.097657c.078125-2.148437-.480469-4.269531-1.601562-6.101562l-30.898438-46.800781 30.898438-46.800781c1.222656-1.785157 1.789062-3.941407 1.601562-6.097657l-3.101562-56.101562 49.902343-25.199219c1.902344-.953125 3.445313-2.496094 4.398438-4.398438l25.199219-49.902343 56.101562 3.101562c2.144531.074219 4.265625-.480469 6.101562-1.601562l46.796876-30.898438 46.800781 30.898438c1.789062 1.222656 3.945312 1.789062 6.101562 1.601562l56.097657-3.101562 25.203124 49.902343c.953126 1.902344 2.496094 3.445313 4.398438 4.398438l49.898438 25.199219-3.097657 56.101562c-.078125 2.144531.480469 4.265625 1.597657 6.097657l30.902343 46.800781zm0 0' />
              </svg>
              <div className='dashboard-home__card__texts'>
                <h1 className='dashboard-home__card__texts__heading'>
                  {dashboard?.totalCertificates}
                </h1>
                <p className='dashboard-home__card__texts__sub-heading'>
                  Certificates Issued
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className='d-flex justify-content-between align-items-center mt-3'>
          {graphData && chartData && (
            <ExcelFile
              element={
                <button className='float-right display-6 py-2'>
                  Download Excel
                </button>
              }
            >
              <ExcelSheet data={graphData} name='Analysis By Product'>
                <ExcelColumn label='Product Code' value='name' />
                <ExcelColumn label='Travel Inquires' value='inquiries' />
                <ExcelColumn label='Certificates' value='certificates' />
                <ExcelColumn label='Payments' value='payments' />
              </ExcelSheet>
              <ExcelSheet data={chartData.type || []} name='Analysis By Type'>
                <ExcelColumn label='Group' value='name' />
                <ExcelColumn label='Amount' value='value' />
              </ExcelSheet>
              <ExcelSheet
                data={chartData.purpose || []}
                name='Analysis By Purpose'
              >
                <ExcelColumn label='Group' value='name' />
                <ExcelColumn label='Amount' value='value' />
              </ExcelSheet>
            </ExcelFile>
          )}

          <div className='p-0'>
            <select
              className='form-control'
              style={{ width: '200px', fontSize: '1.3rem' }}
              value={period}
              onChange={e => setPeriod(e.target.value)}
            >
              <option value='all'>All</option>
              <option value='today'>Today</option>
              <option value='yesterday'>Yesterday</option>
              <option value='this-week'>This Week</option>
              <option value='last-week'>Last Week</option>
              <option value='this-month'>This Month</option>
              <option value='last-month'>Last Month</option>
              <option value='this-year'>This Year</option>
              <option value='last-year'>Last Year</option>
            </select>
          </div>
        </div>

        <div className='dashboard-home__tabs'>
          <div className='dashboard-home__tabs__body'>
            <div className='card mt-5'>
              <div className='card-header card-title py-4'>
                Certificates and Inquires
              </div>
              <div style={{ height: '400px' }} className='card-body py-5'>
                <ResponsiveContainer width='100%' height='100%'>
                  <AreaChart
                    width={500}
                    height={400}
                    data={graphData}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 0,
                      bottom: 0
                    }}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='name' />
                    <YAxis />
                    <Tooltip
                      formatter={value =>
                        new Intl.NumberFormat('en').format(value)
                      }
                    />
                    <Area
                      type='monotone'
                      dataKey='inquiries'
                      stroke='#594fc9'
                      fill='#594fc966'
                      strokeWidth='2'
                      fillOpacity='0.3'
                      strokeOpacity='0.6'
                    />
                    <Area
                      type='monotone'
                      dataKey='certificates'
                      stroke='#1c9c85'
                      strokeWidth='2'
                      fill='#1c9c8566'
                      fillOpacity='0.3'
                      strokeOpacity='0.6'
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className='card mt-5'>
              <div className='card-header card-title py-4'>Payments</div>
              <div style={{ height: '400px' }} className='card-body py-5'>
                <ResponsiveContainer width='100%' height='100%'>
                  <AreaChart
                    width={500}
                    height={400}
                    data={graphData}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 0,
                      bottom: 0
                    }}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='name' />
                    <YAxis />
                    <Tooltip
                      formatter={value =>
                        '₦' + new Intl.NumberFormat('en').format(value)
                      }
                    />
                    <Area
                      type='monotone'
                      dataKey='payments'
                      stroke='#594fc9'
                      fill='#594fc966'
                      strokeWidth='2'
                      fillOpacity='0.3'
                      strokeOpacity='0.6'
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className='row'>
              <div className='col-md-6'>
                <div className='card mt-5'>
                  <div className='card-header card-title py-4'>
                    Certificate by Payment Type
                  </div>
                  <div style={{ height: '400px' }} className='card-body py-5'>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          // activeIndex={activeIndex}
                          // activeShape={radiusPart}
                          dataKey='value'
                          data={chartData?.type}
                          spacing='2'
                          // onMouseEnter={(_, index) => setActiveIndex(_, index)}
                          fill='#594fc9'
                          label={d => `${d.name}: ${d.value}`}
                        />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='card mt-5'>
                  <div className='card-header card-title py-4'>
                    Certificate by Travel Purpose
                  </div>
                  <div style={{ height: '400px' }} className='card-body py-5'>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          dataKey='value'
                          data={chartData?.purpose}
                          fill='#1c9c85'
                          label={d => `${d.name}: ${d.value}`}
                        />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </RetryLoader>
    </div>
  );
};

export default Reports;
